/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import type { ViewEvent } from 'client-code/livelog-events';

import { MILLISECONDS_IN_A_SECOND } from '../../../config';
import { DeviceModel } from '../../device';
import type { ILivelogEventsService } from '../../interfaces/ILivelogEventsService';
import { DateTime } from '../../utils/DateTime';
import { Log } from '../Log';
import { ViewEventsModel } from './ViewEventsModel';
/**
 * ISO 3166-1 alpha-2 → alpha-3 country code map.
 * Extend as needed when new regions are supported.
 */
const ISO2_TO_ISO3: Record<string, string> = {
    CA: 'CAN',
    PA: 'PAN',
    US: 'USA',
};

const DEFAULT_COUNTRY_CODE = 'USA';

/**
 * Public static API for the CloudCore Live Logging feature.
 *
 * All callers should use **only this class** to send ViewEvents.
 *
 * @example
 * ```ts
 * // When video playback starts:
 * LiveLogger.setViewStartTime(Date.now());
 *
 * // When video session ends:
 * LiveLogger.logViewEvents(serviceParams, sessionType, itemId, ...);
 * ```
 */
export class LiveLogger {
    private static TAG = '[livelogEvent] LiveLogger';

    /** Epoch-ms when the current view session started. */
    private static viewStartTime: number = 0;

    /** Lazy singleton view events model. */
    private static viewEventsModel: ViewEventsModel | null = null;

    /**
     * Guards against sending duplicate ViewEvents for the same session.
     * Set to true after logViewEvents successfully buffers an event.
     * Reset by setViewStartTime() when a new watch session starts.
     */
    private static viewEventSent: boolean = false;

    // ──────────────────────────────────────────────
    // Session identity (written by VPC, read by triggerViewEvent)
    // ──────────────────────────────────────────────

    /** Session type: "LiveTv" | "Socu" | "Dvr" | "Vod". Set by VPC. */
    public static sessionType: string = '';

    /** Content ID for the currently playing item. Set by VPC. */
    public static contentId: string = '';

    /** Station / channel ID (optional). Set by VPC. */
    public static channelId: string | undefined = undefined;

    /**
     * Epoch-ms of the airing start time (for LINEAR/DVR spec fields).
     * Derived by VPC from AiringOffer[0].startTime.
     */
    public static airingStartTimeMs: number | undefined = undefined;

    // ──────────────────────────────────────────────
    // Service injection
    // ──────────────────────────────────────────────

    /** LiveLog events service, injected once post sign-in. */
    private static livelogEventsService: ILivelogEventsService | null = null;

    /**
     * Injects the LiveLog events service.
     * Must be called once after post-sign-in SLS endpoints are ready.
     */
    public static setLivelogEventsService(service: ILivelogEventsService | null): void {
        LiveLogger.livelogEventsService = service;
    }

    /**
     * Returns the current LiveLog events service, or `null` if not yet set.
     */
    public static getLivelogEventsService(): ILivelogEventsService | null {
        return LiveLogger.livelogEventsService;
    }

    // ──────────────────────────────────────────────
    // Session lifecycle
    // ──────────────────────────────────────────────

    /**
     * Stores session identity data for the current playback session.
     * Called by VideoPlaybackController at each logWatchContentStart site.
     */
    public static setSessionData(
        sessionType: string,
        contentId: string,
        channelId?: string,
        airingStartTimeMs?: number,
    ): void {
        LiveLogger.sessionType = sessionType;
        LiveLogger.contentId = contentId;
        LiveLogger.channelId = channelId;
        LiveLogger.airingStartTimeMs = airingStartTimeMs;
    }

    /**
     * Resets all LiveLogger session-scoped state.
     * Called by VideoPlaybackController when a watch session ends.
     */
    public static resetSession(): void {
        LiveLogger.sessionType = '';
        LiveLogger.contentId = '';
        LiveLogger.channelId = undefined;
        LiveLogger.airingStartTimeMs = undefined;
        LiveLogger.viewStartTime = 0;
        LiveLogger.viewEventSent = false;
    }

    // ──────────────────────────────────────────────
    // ViewEvent helpers
    // ──────────────────────────────────────────────

    /**
     * Records the start time of the current viewing session.
     * Must be called when video playback begins.
     *
     * @param startTimeMs - Epoch milliseconds (e.g. `Date.now()`).
     */
    public static setViewStartTime(startTimeMs: number): void {
        if (startTimeMs !== LiveLogger.viewStartTime) {
            LiveLogger.viewEventSent = false;
        }
        LiveLogger.viewStartTime = startTimeMs;
        Log.i(LiveLogger.TAG, `setViewStartTime: ${startTimeMs}`);
    }

    /**
     * Builds and sends a ViewEvent using session data owned by LiveLogger.
     *
     * This is the single entry-point for livelog event firing.
     * Neither QoEAppLogger nor VideoPlaybackController is imported here —
     * VPC writes session data via setSessionData() and calls this method
     * when a session ends.
     *
     * @param seekRange - Player seek range, provided by VideoPlaybackController.
     */
    public static triggerViewEvent(seekRange?: { start: number; end: number }): void {
        const { livelogEventsService } = LiveLogger;
        if (!livelogEventsService) {
            Log.i(
                LiveLogger.TAG,
                '[livelogEvent] triggerViewEvent: livelogEventsService not set — skipping',
            );
            return;
        }

        const { sessionType, contentId, channelId, airingStartTimeMs } = LiveLogger;

        // Use LiveLogger.viewStartTime — the private authoritative copy written by
        // setViewStartTime(). This is never zeroed by an external reset, so it is
        // safe to use for duration math even if resetSession() was called mid-session.
        const wallClockDurationMs =
            LiveLogger.viewStartTime > 0 ? Date.now() - LiveLogger.viewStartTime : 0;
        const viewDurationSecs = wallClockDurationMs / MILLISECONDS_IN_A_SECOND;

        const contentDurationSecs = seekRange ? seekRange.end - seekRange.start : 0;
        const startOffsetSecs = seekRange ? seekRange.start : 0;
        // Best available estimate: seekRange.end represents the live/DVR edge, i.e. the last
        // playable position — a reasonable proxy for where the user ended up.
        const endOffsetSecs = seekRange ? seekRange.end : 0;

        Log.i(
            LiveLogger.TAG,
            `[livelogEvent] triggerViewEvent: sessionType=${sessionType}, contentId=${contentId}, viewDurationSecs=${viewDurationSecs}, contentDurationSecs=${contentDurationSecs}`,
        );

        LiveLogger.logViewEvents(
            livelogEventsService,
            sessionType,
            contentId,
            viewDurationSecs,
            contentDurationSecs,
            startOffsetSecs,
            endOffsetSecs,
            channelId,
            airingStartTimeMs,
        );
    }

    /**
     * Builds and buffers a ViewEvent after a video session ends.
     *
     * @param livelogEventsService - The livelog events service instance.
     * @param sessionType - e.g. "LiveTv", "Vod", "Dvr", "StartOver", "CatchUp".
     * @param itemId - Content ID (e.g. "tivo:ct.12345").
     * @param viewDurationSecs - Actual seconds of video played at normal speed.
     * @param contentDurationSecs - Total content duration in seconds.
     * @param startOffsetSecs - Earliest offset (seconds) the user viewed from.
     * @param endOffsetSecs - Latest offset (seconds) the user viewed to.
     * @param stationId - Station ID for LINEAR/DVR/NDVR events (optional).
     * @param airingStartTimeMs - Epoch ms when the programme started airing (optional).
     * @param vodProviderId - VOD provider ID for VOD events (optional).
     */
    public static logViewEvents(
        livelogEventsService: ILivelogEventsService,
        sessionType: string,
        itemId: string,
        viewDurationSecs: number,
        contentDurationSecs: number,
        startOffsetSecs: number,
        endOffsetSecs: number,
        stationId?: string,
        airingStartTimeMs?: number,
        vodProviderId?: string,
    ): void {
        const viewEventType = LiveLogger.mapSessionTypeToViewEventType(sessionType);

        const now = Date.now();

        // Guard against sending duplicate ViewEvents for the same session.
        // e.g. END_OF_CONTENT fires first, then CHANNEL_CHANGE fires for the same session.
        if (LiveLogger.viewEventSent) {
            Log.i(
                LiveLogger.TAG,
                `logViewEvents: duplicate event suppressed for itemId=${itemId} — viewEventSent already true`,
            );
            return;
        }

        // The spec requires all numeric fields (viewDuration, contentDuration,
        // startOffset, endOffset) to be integers. Round all float values.
        // startOffsetSecs / endOffsetSecs from the player are HLS epoch-based
        // timestamps (seconds since Unix epoch), so we normalise them to
        // content-relative offsets by subtracting startOffsetSecs.
        const contentDurationInt = Math.round(contentDurationSecs);
        const viewDurationInt = Math.round(viewDurationSecs);

        // The spec requires viewDuration to be between 120 and 14400 seconds.
        // Skip sending events that don't meet the minimum threshold.
        const MIN_VIEW_DURATION_SECS = 120;
        if (viewDurationInt < MIN_VIEW_DURATION_SECS) {
            Log.i(
                LiveLogger.TAG,
                `logViewEvents: viewDuration ${viewDurationInt}s is below minimum ${MIN_VIEW_DURATION_SECS}s — skipping event`,
            );
            return;
        }

        // Skip when seekRange was missing or invalid — contentDuration=0 would make
        // startOffset/endOffset inconsistent and the backend would reject the event.
        if (contentDurationInt <= 0) {
            Log.i(
                LiveLogger.TAG,
                `logViewEvents: contentDuration is ${contentDurationInt}s (seekRange missing or invalid) — skipping event`,
            );
            return;
        }

        // Spec: airingStartTime MUST be set for LINEAR, MULTIVIEW_LINEAR, DVR_PLAY and NDVR_PLAY.
        // If it is absent for those types the backend will reject with 400.
        const requiresAiringStartTime =
            viewEventType === 'LINEAR' ||
            viewEventType === 'MULTIVIEW_LINEAR' ||
            viewEventType === 'DVR_PLAY' ||
            viewEventType === 'NDVR_PLAY';
        if (requiresAiringStartTime && !airingStartTimeMs) {
            Log.i(
                LiveLogger.TAG,
                `logViewEvents: airingStartTime is required for type=${viewEventType} but not set — skipping event`,
            );
            return;
        }

        // Both are clamped within [0, contentDurationInt] so that startOffset < endOffset
        // is always satisfied and endOffset never exceeds contentDuration.
        const startOffsetInt = 0;
        const endOffsetInt = Math.min(
            Math.max(1, Math.round(endOffsetSecs - startOffsetSecs)),
            contentDurationInt,
        );

        const viewEvent: ViewEvent = {
            type: viewEventType,
            startTime: LiveLogger.viewStartTime,
            endTime: now,
            itemId,
            deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
            product: DeviceModel.getTiVoSettings().getAppName(),
            viewDuration: viewDurationInt,
            contentDuration: contentDurationInt,
            portionViewed:
                contentDurationInt > 0
                    ? Math.round((10000 * viewDurationInt) / contentDurationInt)
                    : 0,
            startOffset: startOffsetInt,
            endOffset: endOffsetInt,
            stationId: stationId || undefined,
            airingStartTime: airingStartTimeMs || undefined,
            vodProviderId: vodProviderId || undefined,
            utcOffset: DateTime.getUtcOffsetInHHMM(),
            country: LiveLogger.getIso3CountryCode(),
        };

        Log.i(
            LiveLogger.TAG,
            `logViewEvents: type=${viewEventType}, itemId=${itemId}, viewDuration=${viewDurationInt}s, contentDuration=${contentDurationInt}s, endOffset=${endOffsetInt}`,
        );

        LiveLogger.viewEventSent = true;
        if (!LiveLogger.viewEventsModel) {
            LiveLogger.viewEventsModel = new ViewEventsModel(livelogEventsService);
        }
        LiveLogger.viewEventsModel!.uploadEvent(viewEvent);
    }

    // ──────────────────────────────────────────────
    // Session-type → ViewEventType mapping
    // ──────────────────────────────────────────────

    /**
     * Maps a session type string to the corresponding ViewEventType value.
     *
     * @param sessionType - e.g. "LiveTv", "Vod", "Dvr", "StartOver", "CatchUp".
     * @returns The ViewEvent.type string.
     */
    private static mapSessionTypeToViewEventType(sessionType: string): ViewEvent['type'] {
        switch (sessionType) {
            case 'LiveTv':
                return 'LINEAR';
            case 'Vod':
                return 'VOD_SUBSCRIPTION';
            case 'Dvr':
                return 'NDVR_PLAY';
            case 'StartOver':
            case 'CatchUp':
            case 'Socu':
                return 'STARTOVER_CATCHUP';
            default:
                return 'LINEAR';
        }
    }

    // ──────────────────────────────────────────────
    // Utility helpers
    // ──────────────────────────────────────────────

    /**
     * Converts the device locale's ISO 3166-1 alpha-2 country code to alpha-3.
     * Falls back to "USA" if the code is not mapped.
     */
    private static getIso3CountryCode(): string {
        try {
            const locale = DeviceModel.getTVProperties().getLocale(); // e.g. "en_US"
            const parts = locale.split('_');
            const alpha2 = parts.length > 1 ? parts[1].toUpperCase() : '';
            return ISO2_TO_ISO3[alpha2] || DEFAULT_COUNTRY_CODE;
        } catch {
            return DEFAULT_COUNTRY_CODE;
        }
    }
}
